import { EntityInfoUtils } from '@record-it-npm/synchro-common';
import { RepositoryByEntityName } from './EntityRepositoryContainer';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';

export class EntityRepositorySorter<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  TRepositoryByEntityName extends RepositoryByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >
> {
  constructor(
    private readonly repositories: Array<
      TRepositoryByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]
    >
  ) {}

  public getSortedRepositories(): Array<
    TRepositoryByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]
  > {
    let missingInfos = this.getRepositorySortInfos();
    const sortedInfos: Array<
      RepositorySortInfo<
        TSynchronizationEnvironmentTypes,
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName,
        TRepositoryByEntityName
      >
    > = [];

    let tries = 0;
    do {
      tries++;

      missingInfos = this.sortOnce(sortedInfos, missingInfos);

      if (tries > 200) {
        throw new RepositoriesCouldNotBeSortedError({
          maxSortTries: 200,
          missingInfos
        });
      }
    } while (missingInfos.length);

    return sortedInfos.map((info) => info.repository);
  }

  private getRepositorySortInfos(): Array<
    RepositorySortInfo<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName,
      TRepositoryByEntityName
    >
  > {
    return this.repositories.map<
      RepositorySortInfo<
        TSynchronizationEnvironmentTypes,
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName,
        TRepositoryByEntityName
      >
    >((r) => {
      const entityInfo = r.getEntityInfo();
      return {
        entityName: entityInfo.entityName,
        referencedEntities: EntityInfoUtils.getAllRefFieldInfos(entityInfo)
          .map((i) => i.ref)
          .filter((i) => i !== entityInfo.entityName),
        repository: r
      };
    });
  }

  /**
   * @param sortedInfos - Gets modified in place. This is where all infos should be sorted into.
   * @returns - infos which couldn't be sorted yet
   */
  private sortOnce(
    sortedInfos: Array<
      RepositorySortInfo<
        TSynchronizationEnvironmentTypes,
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName,
        TRepositoryByEntityName
      >
    >,
    remainingInfos: Array<
      RepositorySortInfo<
        TSynchronizationEnvironmentTypes,
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName,
        TRepositoryByEntityName
      >
    >
  ): Array<
    RepositorySortInfo<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName,
      TRepositoryByEntityName
    >
  > {
    const missingInfos: Array<
      RepositorySortInfo<
        TSynchronizationEnvironmentTypes,
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName,
        TRepositoryByEntityName
      >
    > = [];

    for (const info of remainingInfos) {
      const dependenciesFullfilled = info.referencedEntities.every(
        (referencedEntityName) =>
          sortedInfos.find(
            (sortedInfo) => sortedInfo.entityName === referencedEntityName
          )
      );
      if (!dependenciesFullfilled) {
        missingInfos.push(info);
        continue;
      }

      const firstDependendIndex = sortedInfos.findIndex((sortedInfo) =>
        sortedInfo.referencedEntities.includes(info.entityName)
      );
      if (firstDependendIndex >= 0) {
        sortedInfos.splice(firstDependendIndex, 0, info);
      } else {
        sortedInfos.push(info);
      }
    }

    return missingInfos;
  }
}

export class RepositoriesCouldNotBeSortedError extends Error {
  constructor({
    maxSortTries,
    missingInfos
  }: {
    maxSortTries: number;
    missingInfos: Array<
      RepositorySortInfo<
        AnySynchronizationEnvironmentTypesConstraint,
        any,
        any,
        any
      >
    >;
  }) {
    const entityNames = missingInfos.map((info) => info.entityName).join('; ');

    super(
      `Repositories couldn't be sorted in ${maxSortTries} tries. This can happen if you have cyclic dependencies or you have an extremely deep dependency tree. Unsorted EntityNames: ${entityNames}`
    );
  }
}

type RepositorySortInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  TRepositoryByEntityName extends RepositoryByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >
> = {
  entityName: TEntityName;
  referencedEntities: Array<
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >;
  repository: TRepositoryByEntityName[TEntityName];
};
